import {
  API_REJECTED,
  BINDER_EXPIRED_STATUS,
  BINDER_RESCINDED,
  BOUND_STATUS,
  DECLINED_STATUS,
  DELETED_STATUS,
  DRAFT_STATUS,
  EXPIRED_STATUS,
  IN_FORCE_STATUS,
  INVALID_STATUS,
  ISSUED_STATUS,
  LOST_STATUS,
  TEMPORARY_STATUS,
  VOID_STATUS,
} from '../console/_statics/quote.statics';

export type AgentSourceKind =
  | 'policy'
  | 'binder'
  | 'hardLink'
  | 'quote'
  | 'interested'
  | 'none';

/**
 * A policy, binder or quote — each carries the agent that sold it.
 *
 * The status lives under a different key per endpoint: `/api/policy/v1/search`
 * stamps `status`, `/api/quote/v1/search` stamps `agencyStatus` and
 * `customerStatus` and never `status`. Read it with `statusOf`, never directly.
 */
interface AgentBearingRecord {
  readonly agencyId?: string;
  readonly agencyName?: string;
  readonly agentFirstName?: string;
  readonly agentLastName?: string;
  readonly agentEmail?: string;
  readonly agentPhone?: string;
  readonly status?: string;
  readonly agencyStatus?: string;
  readonly customerStatus?: string;
}

/** One entry of `GET /agency/myagent` — the account's hard-linked agency. */
interface HardLink {
  readonly agencyId?: string;
  readonly name?: string;
  readonly agentFirstName?: string;
  readonly agentLastName?: string;
  readonly agentEmail?: string;
  readonly agentPhone?: string;
}

interface InterestedAgency {
  readonly id?: string;
  readonly name?: string;
}

export interface ResolveAgentInput {
  readonly policies: readonly AgentBearingRecord[];
  readonly binders: readonly AgentBearingRecord[];
  readonly quotes: readonly AgentBearingRecord[];
  readonly hardLink: HardLink | null;
  readonly interested: readonly InterestedAgency[];
}

export interface ResolvedAgent {
  readonly kind: AgentSourceKind;
  readonly agencyId?: string;
  readonly agencyName?: string;
  readonly agentFullName?: string;
  readonly agentEmail?: string;
  readonly agentPhone?: string;
}

export interface ResolvedAgents {
  readonly primary: ResolvedAgent;
  /**
   * Spec bullet 3: a hard-linked agency and a live quote are shown together, so
   * the quote's agent is carried here rather than being suppressed by the link.
   */
  readonly secondary?: ResolvedAgent;
}

/**
 * One spelling per status. The backend vocabularies disagree with themselves:
 * quote.statics says 'BINDER EXPIRED' while QuoteAgencyStatus says
 * 'BINDER_EXPIRED', and both reach this resolver.
 */
const normalize = (status: string): string =>
  status.toUpperCase().replace(/_/g, ' ');

const POLICY_LIVE_STATUSES: readonly string[] = [
  IN_FORCE_STATUS,
  ISSUED_STATUS,
].map(normalize);
const BINDER_LIVE_STATUSES: readonly string[] = [BOUND_STATUS].map(normalize);

/**
 * Quotes are matched by exclusion rather than by allowlist. The agent and
 * policyholder vocabularies share almost no live value — there is no READY for
 * a customer and no AVAILABLE for an agent — so an allowlist has to enumerate
 * both and silently hides the agent the moment either gains a status. Every
 * quote that is not dead names a real agent, which is the question this page
 * asks.
 */
const DEAD_QUOTE_STATUSES: readonly string[] = [
  DRAFT_STATUS,
  TEMPORARY_STATUS,
  EXPIRED_STATUS,
  BINDER_EXPIRED_STATUS,
  DELETED_STATUS,
  VOID_STATUS,
  INVALID_STATUS,
  DECLINED_STATUS,
  API_REJECTED,
  LOST_STATUS,
  BINDER_RESCINDED,
  // No constant in quote.statics — these reach the quote search from
  // QuoteAgencyStatus and QuoteCustomerStatus only.
  'SUBMISSION_LOST',
  'QUOTE_LOST',
  'BINDER_LOST',
  'CANCEL_PENDING',
  'CANCELLED',
  'FLAT_CANCELLED',
].map(normalize);

const fullName = (first?: string, last?: string): string | undefined => {
  const joined = [first, last].filter(Boolean).join(' ').trim();
  if (!joined) {
    return undefined;
  }
  return joined;
};

/**
 * `agencyStatus` before `customerStatus`: a bound quote reads BOUND to the
 * agent but ACTIVE to the customer, and only the former identifies it as a
 * binder.
 */
const statusOf = (record: AgentBearingRecord): string | undefined =>
  record.status ?? record.agencyStatus ?? record.customerStatus;

const firstWithStatusIn = (
  records: readonly AgentBearingRecord[],
  statuses: readonly string[]
): AgentBearingRecord | undefined =>
  records.find((record) => {
    const status = statusOf(record);
    return !!status && statuses.includes(normalize(status));
  });

const firstLiveQuote = (
  records: readonly AgentBearingRecord[]
): AgentBearingRecord | undefined =>
  records.find((record) => {
    const status = statusOf(record);
    return !!status && !DEAD_QUOTE_STATUSES.includes(normalize(status));
  });

const fromRecord = (
  record: AgentBearingRecord,
  kind: AgentSourceKind
): ResolvedAgent => ({
  kind,
  agencyId: record.agencyId,
  agencyName: record.agencyName,
  agentFullName: fullName(record.agentFirstName, record.agentLastName),
  agentEmail: record.agentEmail,
  agentPhone: record.agentPhone,
});

const fromHardLink = (hardLink: HardLink): ResolvedAgent => ({
  kind: 'hardLink',
  agencyId: hardLink.agencyId,
  agencyName: hardLink.name,
  agentFullName: fullName(hardLink.agentFirstName, hardLink.agentLastName),
  agentEmail: hardLink.agentEmail,
  agentPhone: hardLink.agentPhone,
});

/**
 * Whether the policyholder may look for a new agent. Only an account with no
 * live policy and no hard-linked agency may: either one already ties it to an
 * agent. Uses the same live-policy statuses as `resolveAgentSource`.
 */
export function canSearchForAgent(
  input: Pick<ResolveAgentInput, 'policies' | 'hardLink'>
): boolean {
  const policy = firstWithStatusIn(input.policies, POLICY_LIVE_STATUSES);
  return !policy && !input.hardLink?.agencyId;
}

/**
 * Resolves which agent the policyholder actually has, in the order the spec
 * defines: a live policy wins, then a bound binder, then the hard-linked agency,
 * then a live quote, and finally the interested-agencies list.
 */
export function resolveAgentSource(input: ResolveAgentInput): ResolvedAgents {
  const policy = firstWithStatusIn(input.policies, POLICY_LIVE_STATUSES);
  if (policy) {
    return { primary: fromRecord(policy, 'policy') };
  }

  const binder = firstWithStatusIn(input.binders, BINDER_LIVE_STATUSES);
  if (binder) {
    return { primary: fromRecord(binder, 'binder') };
  }

  const quote = firstLiveQuote(input.quotes);

  if (input.hardLink?.agencyId) {
    const primary = fromHardLink(input.hardLink);
    if (quote && quote.agencyId !== primary.agencyId) {
      return { primary, secondary: fromRecord(quote, 'quote') };
    }
    return { primary };
  }

  if (quote) {
    return { primary: fromRecord(quote, 'quote') };
  }

  if (input.interested.length > 0) {
    return { primary: { kind: 'interested' } };
  }

  return { primary: { kind: 'none' } };
}
